import { ViewType } from "../Models/AppModel.js";
import { bindedModelBase } from "../Models/BindedModel.js";
import { FlipTypeGarage } from '../States/State.js';
import { DraggableLabel } from "../Tools/Primitive/Label/DraggableLabel.js";
import { Utils } from "../Utils/Utils.js";
/**
 * Handle events logic.
 */
export class ContextInitBase {
    /**
     * Init all events logic.
     */
    static create(main, context) {
        context.addEventListener("setView", (event) => {
            main.setView(event.args);
        });
        context.addEventListener("zoomIn", () => {
            main.activeCameraControl.zoomIn();
        });
        context.addEventListener("zoomOut", () => {
            main.activeCameraControl.zoomOut();
        });
        context.addEventListener("fitToView", () => {
            main.activeCameraControl.fitToPlane();
        });
        context.addEventListener("setBrightness", (event) => {
            main.activeTools.plane.brightness = event.args;
            const brightness = main.activeTools.viewType === ViewType.AP ? "brightnessAP" : "brightnessLT";
            main.model[brightness] = event.args;
        });
        context.addEventListener("setContrast", (event) => {
            main.activeTools.plane.contrast = event.args;
            const contrast = main.activeTools.viewType === ViewType.AP ? "contrastAP" : "contrastLT";
            main.model[contrast] = event.args;
        });
        context.addEventListener("startTrackingPlane", () => {
            main.history.startTracking(main.activeTools.viewType, main.activeTools.plane);
        });
        context.addEventListener("detectChangesPlane", () => {
            main.history.detectChanges(main.activeTools.viewType);
        });
        context.addEventListener("startCrop", (event) => {
            main.setView(event.args);
            main.toolsInit.initCropPlaneTool(event.args);
            main.getTools(event.args).cropPlaneTool.start();
            main.activeCameraControl.fitToPlane();
            main.activeCameraControl.zoomOut();
        });
        context.addEventListener("confirmCrop", () => {
            main.activeTools.cropPlaneTool.confirm();
        });
        context.addEventListener("startRotate", (event) => {
            main.setView(event.args);
            main.toolsInit.initRotationPlaneTool(event.args);
            main.getTools(event.args).rotationPlaneTool.start();
            main.activeCameraControl.fitToPlaneRotation();
            main.activeCameraControl.zoomOut();
        });
        context.addEventListener("confirmRotate", () => {
            main.activeTools.rotationPlaneTool.confirm();
        });
        context.addEventListener("setFlip", (event) => {
            main.setFlip(event.args.viewType, event.args.flipType, event.args.flipValue);
        });
        context.addEventListener("setFlipGarage", async (event) => {
            const plane = main.getTools(event.args.viewType).plane;
            if (event.args.flipType === FlipTypeGarage.horizontal) {
                await plane.applyHorizontalFlip();
            }
            else if (event.args.flipType === FlipTypeGarage.vertical) {
                await plane.applyVerticalFlip();
            }
            else if (event.args.flipType === FlipTypeGarage.both) {
                plane.apply180Rotation();
            }
            else {
                console.warn('setFlipGarage: invalid value.');
            }
        });
        context.addEventListener("setViewMultiple", () => {
            main.multiViewsActive = true;
        });
        context.addEventListener("stopRegistering", () => {
            main.activeTools.registerHitPlane.stopRegistering();
            main.model.magnifier = undefined; //todo remove
        });
        context.addEventListener("resetRegistering", () => {
            main.activeTools.registerHitPlane.resetRegistering();
        });
        context.addEventListener("completeRegistering", () => {
            main.model.magnifier = undefined; //todo remove
        });
        context.addEventListener("setVisibility", (event) => {
            main.layers.setLayersVisibility(event.args.key, event.args.value);
        });
        context.addEventListener("addLine", () => {
            const line = main.toolsInit.initLine();
            if (!line)
                return;
            bindedModelBase.layer_lines = true;
        });
        context.addEventListener("addGoniometer", () => {
            const goniometer = main.toolsInit.initCrossGoniometer();
            if (!goniometer)
                return;
            bindedModelBase.layer_goniometers = true;
        });
        context.addEventListener("onBridgeResult", (event) => {
            main.bridge.dispatchEvent({ type: "onResult", args: event.args });
        });
        context.addEventListener('resetLabelsPosition', () => {
            this.resetLabelPosition(main);
        });
        context.addEventListener("deleteMeasurementTool", () => {
            main.deleteMeasurementTool();
        });
        context.addEventListener("startCalibration", (event) => {
            main.setView(event.args);
            const calibrating = event.args === ViewType.AP ? "calibratingAP" : "calibratingLT";
            bindedModelBase[calibrating] = true;
            main.toolsInit.initCalibrationTool(event.args);
            const scaleFactor = event.args === ViewType.AP ? "scaleFactorAP" : "scaleFactorLT";
            bindedModelBase[scaleFactor] = 1;
            const calibTool = main.activeTools.calibrationTool.tool;
            if (calibTool?.reset) { // mettere reset abstract
                calibTool.reset();
            }
            main.activeCameraControl.fitToPlane();
        });
        context.addEventListener("resetCalibration", (event) => {
            const viewType = event.args;
            const calibrating = viewType === ViewType.AP ? "calibratingAP" : "calibratingLT";
            bindedModelBase[calibrating] = false;
            const scaleFactor = viewType === ViewType.AP ? "scaleFactorAP" : "scaleFactorLT";
            bindedModelBase[scaleFactor] = 1;
            const tools = main.getTools(viewType);
            context.handle("setCalibrationRegisteringMode", false);
            tools.registerPointsTool.cancel();
        });
        context.addEventListener("calibrate", (event) => {
            const scaleFactor = bindedModelBase.calibratingAP ? "scaleFactorAP" : "scaleFactorLT";
            const scaleFactorAppModel = bindedModelBase.calibratingAP ? "scaleFactorAP" : "scaleFactorLT";
            const calibrating = bindedModelBase.calibratingAP ? "calibratingAP" : "calibratingLT";
            bindedModelBase[scaleFactor] = main.getTools(bindedModelBase.calibratingAP ? ViewType.AP : ViewType.LT).calibrationTool.getScaleFactor(event.args);
            main.model[scaleFactorAppModel] = bindedModelBase[scaleFactor];
            main.activeCameraControl.fitToPlane();
            bindedModelBase[calibrating] = false;
        });
        context.addEventListener("setCalibrationMode", (event) => {
            const viewType = bindedModelBase.calibratingAP ? ViewType.AP : ViewType.LT;
            const tools = main.getTools(viewType);
            tools.calibrationTool.mode = event.args;
            this.registerPointsForCalibration(main, context, viewType);
        });
        context.addEventListener("setCalibrationCircleFromPoints", (event) => {
            const viewType = bindedModelBase.calibratingAP ? ViewType.AP : ViewType.LT;
            const tools = main.getTools(viewType);
            const points = tools.plane.getPositionsFromPixels(event.args);
            tools.calibrationTool.setCirclePoints(points);
        });
        context.addEventListener("undo", () => {
            main.history.undo();
            main.activeScene.deselectCurrentTool();
            main.toolsAP.needsRender = true;
            main.toolsLT.needsRender = true;
        });
        context.addEventListener("redo", () => {
            main.history.redo();
            main.activeScene.deselectCurrentTool();
            main.toolsAP.needsRender = true;
            main.toolsLT.needsRender = true;
        });
        return context;
    }
    static resetLabelPosition(main) {
        for (const scene of main.scenes) {
            const labels = Utils.getAllObjects(scene, DraggableLabel);
            for (const label of labels) {
                label.resetPosition();
            }
        }
    }
    static registerPointsForCalibration(main, context, viewType) {
        const tools = main.getTools(viewType);
        context.handle("setCalibrationRegisteringMode", true);
        tools.registerPointsTool.start(tools.calibrationTool.tool, main.activeCamera, {
            onRegError: (args) => {
                main.bridge.dispatchEvent({ type: "onResult", args });
            },
            onRegSuccess: () => {
                context.handle("setCalibrationRegisteringMode", false);
            }
        }, true);
    }
}
//# sourceMappingURL=ContextInit.js.map